import React from 'react';
import {
  CompositeReportDefinitionSummary,
  Folder,
  ModelUtils,
  TemplateSummary,
  TemplatingItemSummary,
} from '../models';
import { FileSystemNode } from './FileSystemNode';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { useDroppable } from '@dnd-kit/core';

const useStyles = makeStyles<{ level: number }>(theme => ({
  container: {
    position: 'relative',
  },
  borderLeft: {
    position: 'absolute',
    left: props => theme.spacing(props.level * 3),
    top: theme.spacing(1),
    bottom: theme.spacing(1),
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
}));

interface FileSystemTreeProps {
  templatingItemSummaries: (TemplatingItemSummary | Folder)[];
  folderNodeActions?: (data: Folder) => React.ReactNode;
  templateNodeActions?: (data: TemplateSummary) => React.ReactNode;
  compositeReportDefinitionNodeActions?: (
    data: CompositeReportDefinitionSummary,
  ) => React.ReactNode;
}

const FileSystemTree: React.FC<FileSystemTreeProps> = ({
  templatingItemSummaries,
  folderNodeActions,
  templateNodeActions,
  compositeReportDefinitionNodeActions,
}) => {
  const level = templatingItemSummaries[0]
    ? ModelUtils.getLevel(templatingItemSummaries[0])
    : 0;
  const styles = useStyles({ level: level ?? 0 });

  return (
    <Box direction="column" className={styles.container}>
      <Box className={level > 0 ? styles.borderLeft : ''} />
      {templatingItemSummaries.map(templatingItemSummary => (
        <FileSystemNode
          key={templatingItemSummary.path}
          item={templatingItemSummary}
          folderNodeActions={folderNodeActions}
          templateNodeActions={templateNodeActions}
          compositeReportDefinitionNodeActions={
            compositeReportDefinitionNodeActions
          }
        />
      ))}
    </Box>
  );
};

const useRootStyles = makeStyles<{ isOver: boolean }>(theme => ({
  container: {
    backgroundColor: props =>
      props.isOver ? theme.palette.action.hover : 'transparent',
  },
}));

const RootFileSystemTree: React.FC<FileSystemTreeProps> = ({
  templatingItemSummaries,
  folderNodeActions,
  templateNodeActions,
  compositeReportDefinitionNodeActions,
}) => {
  const { isOver, setNodeRef } = useDroppable({
    id: '$root$',
  });

  const styles = useRootStyles({ isOver });

  return (
    <Box ref={setNodeRef} direction="column" className={styles.container}>
      <FileSystemTree
        templatingItemSummaries={templatingItemSummaries}
        folderNodeActions={folderNodeActions}
        templateNodeActions={templateNodeActions}
        compositeReportDefinitionNodeActions={
          compositeReportDefinitionNodeActions
        }
      />
    </Box>
  );
};

export { FileSystemTree, RootFileSystemTree };
